// MyClientsPage.tsx - FINAL UPGRADED VERSION
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTable, Column, CellProps } from 'react-table';
import { Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import MainCard from '../../../component/MainCard/MainCard';
import useLoadingIndicator from '../../../component/Loader/Loader';
import ShowEntries from '../../../component/ShowEntries/ShowEntries';
import SearchBar from '../../../component/SearchBar/SearchBar';
import * as actions from '../../../Redux/Actions';
import './MyClientsPage.scss';

// Define the final, rich data structure from our API
interface EnrichedClientData {
    _id: string;
    clientName: string;
    openTasks: number;
    overdueTasks: number;
    totalAssignedEmployees: number;
    approvedHc: number;
    actualHc: number;
}

const MyClientsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } = useLoadingIndicator();

    const { getHrbpMyClients } = useSelector((state: any) => state.employeeReducer);
    
    const [pagination, setPagination] = useState({ page: 1, limit: 10 });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearch(search);
            setPagination(p => ({ ...p, page: 1 }));
        }, 500);
        return () => clearTimeout(timerId);
    }, [search]);

    useEffect(() => {
        showLoadingIndicator();
        const filters = { ...pagination, search: debouncedSearch };
        dispatch(actions.getHrbpMyClientsAction(filters, () => {
            hideLoadingIndicator();
        }));
    }, [dispatch, pagination, debouncedSearch]);

    const tableData = useMemo(() => getHrbpMyClients?.data?.data || [], [getHrbpMyClients]);
    const paginationData = useMemo(() => ({
        currentPage: getHrbpMyClients?.data?.currentPage || 1,
        totalPages: getHrbpMyClients?.data?.totalPages || 1,
        totalRecords: getHrbpMyClients?.data?.totalRecords || 0,
    }), [getHrbpMyClients]);

    const handleRowClick = (client: EnrichedClientData) => {
        navigate(`/hrbp-tracker?search=${encodeURIComponent(client.clientName)}`);
    };

    const columns: readonly Column<EnrichedClientData>[] = useMemo(() => [
        {
            Header: 'S.No.',
            id: 'serialNumber',
            Cell: ({ row }: CellProps<EnrichedClientData>) => (
                <div className="text-center">{(pagination.page - 1) * pagination.limit + row.index + 1}</div>
            )
        },
        { Header: 'Client Name', accessor: 'clientName' },
        {
            Header: 'Assigned Employees',
            accessor: 'totalAssignedEmployees',
            Cell: ({ value }) => <div className="text-center font-weight-bold">{value}</div>
        },
        {
            Header: 'Approved HC',
            accessor: 'approvedHc',
            Cell: ({ value }) => <div className="text-center">{value}</div>
        },
        {
            Header: 'Actual HC',
            accessor: 'actualHc',
            Cell: ({ value }) => <div className="text-center">{value}</div>
        },
        {
            Header: 'Open Tasks',
            accessor: 'openTasks',
            Cell: ({ value }) => <div className="text-center task-count">{value}</div>
        },
        {
            Header: 'Overdue Tasks',
            accessor: 'overdueTasks',
            Cell: ({ value }) => (
                <div className={`text-center task-count ${value > 0 ? 'overdue' : ''}`}>{value}</div>
            )
        },
    ], [pagination]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: tableData });

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= paginationData.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handlePageSizeChange = (newSize: number) => {
        setPagination({ page: 1, limit: newSize });
    };

    return (
        <MainCard>
            {loadingComponent()}
            <div className="p-4 my-clients-page">
                <div className="page-header">
                    <h2>My Assigned Clients</h2>
                    <p className="subtitle">A list of all clients assigned to you via your projects.</p>
                </div>
                <div className="filter-bar">
                    <SearchBar filter={search} setFilter={setSearch} placeholder="Search by client name..."  onSearchClicked={() => {}} />
                </div>
                <div className="table-wrapper">
                    <Table hover {...getTableProps()} className="client-table">
                        <thead>
                            {headerGroups.map(hg => (
                                <tr {...hg.getHeaderGroupProps()}>
                                    {hg.headers.map(col => <th {...col.getHeaderProps()}>{col.render('Header')}</th>)}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.length > 0 ? (
                                rows.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} onClick={() => handleRowClick(row.original)} className="clickable-row">
                                            {row.cells.map(cell => <td {...cell.getCellProps()}>{cell.render('Cell')}</td>)}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center p-5">No clients found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                 <div className="d-flex justify-content-between align-items-center mt-3">
                    <ShowEntries pageSize={pagination.limit} setPageSize={handlePageSizeChange} totalEntries={paginationData.totalRecords} />
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${paginationData.currentPage === 1 ? 'disabled' : ''}`}>
                                <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage - 1)}>‹</a>
                            </li>
                            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map(number => (
                                <li key={number} className={`page-item ${paginationData.currentPage === number ? 'active' : ''}`}>
                                    <a className="page-link" href="#" onClick={() => handlePageChange(number)}>{number}</a>
                                </li>
                            ))}
                            <li className={`page-item ${paginationData.currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                                <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage + 1)}>›</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </MainCard>
    );
};

export default MyClientsPage;